import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, KeyRound, Trash2 } from 'lucide-react';
import { api } from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import UserForm from '../../../components/admin/UserForm';
import SendPasswordLinkDialog, { InviteNotice } from '../../../components/admin/SendPasswordLinkDialog';
import ConfirmDialog from '../../../components/admin/ConfirmDialog';
import { RoleBadge, UserStatusBadge } from '../../../components/admin/StatusBadge';
import EmptyState from '../../../components/crm/EmptyState';

// Per-account Users tab (Sprint K-3). A scoped slice of /admin/users that
// shows only the users tied to this account. Reuses the same UserForm,
// SendPasswordLinkDialog, and ConfirmDialog components as the global page so
// behavior + permissions stay identical.
//
// Creating a new user here pre-fills the account_id (and defaults role to
// "client" since this is a school context). The created user automatically
// shows up on the global /admin/users page on its next load — no extra
// plumbing needed; the data layer is the single source of truth.
//
// The global /admin/users page remains the dedicated user-management
// surface (filter by any account, manage staff users, etc.). This tab is
// for "who can log in to this school's portal" at a glance.
export default function UsersTab({ account }) {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sendingLink, setSendingLink] = useState(null);
  const [inviteNotice, setInviteNotice] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/admin/users?account=${encodeURIComponent(account.id)}`);
      setUsers(res?.users || []);
    } catch (err) {
      setError(err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [account.id]);

  useEffect(() => { load(); }, [load]);

  const onSaved = (savedUser, invite) => {
    setUsers((prev) => {
      const idx = prev.findIndex((u) => u.id === savedUser.id);
      if (idx === -1) return [savedUser, ...prev];
      const copy = prev.slice();
      copy[idx] = { ...copy[idx], ...savedUser };
      return copy;
    });
    setFormOpen(false);
    if (invite) setInviteNotice({ email: savedUser.email, result: invite });
  };

  const onDelete = async (u) => {
    try {
      await api.delete(`/api/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (err) {
      alert(err?.message || 'Delete failed');
    }
    setConfirmDelete(null);
  };

  // For the "+ New user" button: seed the form with the account context so
  // admins/ops don't have to search for the school they're already on.
  // role defaults to 'client' (school portal user); they can switch it,
  // and the form auto-clears accountId if they pick a non-client role.
  const newUserSeed = useMemo(() => ({
    role: 'client',
    accountId: account.id,
  }), [account.id]);

  if (loading && users.length === 0) {
    return <div className="animate-pulse h-16 rounded-lg bg-white/[0.03]" />;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12px] text-gray-500">
          {users.length} user{users.length === 1 ? '' : 's'} for {account.name}
        </div>
        <button
          onClick={() => { setEditing(null); setFormOpen(true); }}
          data-testid="account-users-new"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-gray-900 bg-[#B0FC38]/80 hover:bg-[#B0FC38]"
        >
          <Plus className="w-3 h-3" /> New user
        </button>
      </div>

      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[12px] text-red-300/90">
          {error}
        </div>
      )}

      {users.length === 0 ? (
        <EmptyState
          title="No users yet"
          description="Create the first portal login for this account."
          action={
            <button
              onClick={() => { setEditing(null); setFormOpen(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-gray-900 bg-[#B0FC38]/80 hover:bg-[#B0FC38]"
            >
              <Plus className="w-3 h-3" /> Add user
            </button>
          }
        />
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <div
              key={u.id}
              className="flex items-center gap-3 p-3 rounded-xl border border-white/[0.06] bg-white/[0.02]"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] text-white font-medium truncate">{u.name}</span>
                  {u.id === currentUser?.id && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#B0FC38]/[0.15] border border-[#B0FC38]/30 text-[#B0FC38] tracking-wider uppercase">
                      You
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-500 truncate">{u.email}</div>
              </div>
              <RoleBadge role={u.role} />
              <UserStatusBadge user={{ ...u, isActive: Boolean(u.isActive) }} />
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { setEditing(u); setFormOpen(true); }}
                  className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05]"
                  title="Edit user"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setSendingLink(u)}
                  disabled={!u.isActive}
                  className="p-1.5 rounded-lg text-gray-500 hover:text-[#B0FC38] hover:bg-[#B0FC38]/[0.08] disabled:opacity-30 disabled:pointer-events-none"
                  title={!u.isActive ? 'Re-enable this user to send a link' : u.passwordSet === false ? 'Resend invite' : 'Send password reset link'}
                  aria-label={u.passwordSet === false ? `Resend invite to ${u.name}` : `Send password reset link to ${u.name}`}
                >
                  <KeyRound className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setConfirmDelete(u)}
                  disabled={u.id === currentUser?.id}
                  className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/[0.05] disabled:opacity-30 disabled:pointer-events-none"
                  title={u.id === currentUser?.id ? "You can't delete your own account" : 'Delete user'}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <UserForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={onSaved}
        initial={editing || newUserSeed}
        // Pin the account for both create + edit flows opened from this
        // tab — admins/ops shouldn't accidentally reassign a user to a
        // different school while working in this account's context. If
        // they need to move a user, the global /admin/users page is the
        // intended surface.
        lockedAccount={{ id: account.id, name: account.name }}
      />

      <SendPasswordLinkDialog
        open={Boolean(sendingLink)}
        user={sendingLink}
        onClose={() => setSendingLink(null)}
        onSent={() => load()}
      />
      <InviteNotice notice={inviteNotice} onClose={() => setInviteNotice(null)} />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Delete user?"
        description={
          confirmDelete
            ? `This permanently deletes ${confirmDelete.name} (${confirmDelete.email}) and revokes all their sessions.`
            : ''
        }
        confirmLabel="Delete user"
        destructive
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => onDelete(confirmDelete)}
      />
    </div>
  );
}
